import React, { useEffect, useMemo } from 'react'
import { ENTITY_TYPES, getTouchedObject } from '@sales-center/common'
import { useCheckPermissions } from '@user/store/authorization/permissions/useCheckPermissions'
import { useFormikContext } from 'formik'
import { getHours, getMinutes, isBefore, set } from 'date-fns'
import { ActivityFormFieldsDefault } from './ActivityFormFieldsDefault'
import { ActivityFormFieldsAttachMode } from './ActivityFormFieldsAttachMode'
import type { ActivityFormModel, OwnerOption } from './ActivityFormFields.types'

type Props = {
  entityType: ENTITY_TYPES
  quantity?: 'single' | 'bulk'
  salesTeamId: number
  ownerId?: string
  owners?: OwnerOption[]
  // SH-1428: calendar "Create Event" mode — no pre-selected entity. The
  // Attach-To picker (rendered via attachToSlot) sets entityId/entityType on
  // the Formik model, which then drives entity-dependent fields below.
  attachToMode?: boolean
  attachToSlot?: React.ReactNode
}

export function ActivityFormFields(props: Props) {
  const { entityType, quantity, salesTeamId, ownerId, owners, attachToMode } =
    props
  const { attachToSlot } = props
  const isBulk = quantity === 'bulk'

  const {
    setTouched,
    validateForm,
    submitCount,
    values,
    setFieldValue,
    initialValues,
  } = useFormikContext<ActivityFormModel>()

  // In attach mode the entity is selected in the form; otherwise it is fixed
  // by the host. Entity-dependent UI (ECD checkbox, update permission) follows
  // the selected type. Outside attach mode this is identical to the prop.
  const selectedEntityType: ENTITY_TYPES =
    attachToMode && values.entityType != null ? values.entityType : entityType

  const showExpectedClosingDate =
    selectedEntityType === ENTITY_TYPES.Opportunity

  // SH-2338: Attach-To is optional, so in attach mode there may be no record at
  // all. The "Set as Next Action Date" / "Set as Expected Closing Date"
  // checkboxes have nothing to write to in that state, so they are hidden by
  // narrowing the canUpdate PROP below — both checkboxes render inside that
  // guard, so narrowing it alone hides both.
  //
  // The local canUpdate and the clearing effect stay purely permission-based on
  // purpose: driving them off the selection instead would make the effect wipe
  // the setDueDateAsNextActionDate default on mount, and a user who then
  // attached a lead would silently lose it.
  const hasSelectedEntity = !attachToMode || values.entityId != null

  const { dueDate, dueTime } = values

  useEffect(() => {
    if (submitCount === 0) return

    validateForm().then((errors) => {
      setTouched(getTouchedObject(errors))
    })
  }, [submitCount, setTouched, validateForm, getTouchedObject])

  const [canUpdateLeads, canUpdateOpps] = useCheckPermissions(
    'leads_update',
    'opportunities_update',
  )

  // With nothing attached yet there is no record whose permission could apply, so neither answer
  // is meaningful — and resolving it to the Lead permission is actively wrong. selectedEntityType
  // falls back to the entityType prop (Lead), so a user holding opportunities_update but not
  // leads_update got canUpdate === false on mount, and the clearing effect below wiped the
  // setDueDateAsNextActionDate default. Attaching an Opportunity afterwards then showed the
  // checkbox unchecked and silently skipped the Next Action Date write, unlike every other create
  // path. Defer to "either permission" until a record is actually chosen (PR #5056 review).
  const canUpdate = !hasSelectedEntity
    ? canUpdateLeads || canUpdateOpps
    : selectedEntityType === ENTITY_TYPES.Lead
      ? canUpdateLeads
      : canUpdateOpps

  // A conditionally-rendered checkbox keeps its Formik value after it unmounts.
  // In attach mode the entity (and thus canUpdate / showExpectedClosingDate) can
  // flip after selection, so clear these when their checkbox is not shown —
  // otherwise a hidden control would still ship true for a record the user
  // cannot update.
  useEffect(() => {
    if (!canUpdate) {
      if (values.setDueDateAsNextActionDate)
        setFieldValue('setDueDateAsNextActionDate', false)
      if (values.setDueDateAsExpectedClosingDate)
        setFieldValue('setDueDateAsExpectedClosingDate', false)
    } else if (
      !showExpectedClosingDate &&
      values.setDueDateAsExpectedClosingDate
    ) {
      setFieldValue('setDueDateAsExpectedClosingDate', false)
    }
  }, [
    canUpdate,
    showExpectedClosingDate,
    values.setDueDateAsNextActionDate,
    values.setDueDateAsExpectedClosingDate,
    setFieldValue,
  ])

  const isDueDateInPast = useMemo(() => {
    if (!dueDate || !dueTime) return false
    const due = set(new Date(dueDate), {
      hours: getHours(dueTime),
      minutes: getMinutes(dueTime),
    })
    const now = new Date()
    return isBefore(due, now)
  }, [dueDate, dueTime])

  // Effect: If due date/time is in the past, set reminder to 'None' and cancel if editing
  useEffect(() => {
    if (isDueDateInPast && values.reminderThreshold !== 'None') {
      setFieldValue('reminderThreshold', 'None')
    }
  }, [
    isDueDateInPast,
    values.reminderThreshold,
    setFieldValue,
    initialValues.reminderThreshold,
  ])

  if (attachToMode) {
    return (
      <ActivityFormFieldsAttachMode
        salesTeamId={salesTeamId}
        ownerId={ownerId}
        owners={owners}
        entityType={selectedEntityType}
        attachToSlot={attachToSlot}
        canUpdate={canUpdate && hasSelectedEntity}
        showExpectedClosingDate={showExpectedClosingDate}
        reminderDisabled={isDueDateInPast}
      />
    )
  }

  return (
    <ActivityFormFieldsDefault
      salesTeamId={salesTeamId}
      ownerId={ownerId}
      owners={owners}
      entityType={selectedEntityType}
      isBulk={isBulk}
      canUpdate={canUpdate}
      showExpectedClosingDate={showExpectedClosingDate}
      reminderDisabled={isDueDateInPast}
    />
  )
}
